import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { HeartHandshake, Search } from 'lucide-react';
import { appId, db } from '@/lib/firebase';

interface AdminDonorsProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface TimestampLike {
  toDate: () => Date;
}

interface DonorRollupRecord {
  id: string;
  userId: string | null;
  stripeCustomerId: string | null;
  email: string | null;
  name: string | null;
  totalDonated: number;
  donationCount: number;
  monthlyDonationCount: number;
  oneTimeDonationCount: number;
  lastDonationAt: TimestampLike | null;
  lastDonationAmount: number;
  lastDonationFrequency: 'monthly' | 'one_time' | null;
  currency: string;
}

type FrequencyFilter = 'all' | 'monthly' | 'one_time';

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDateTime(ts: TimestampLike | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function AdminDonors(_props: Readonly<AdminDonorsProps>): JSX.Element {
  const [donors, setDonors] = useState<DonorRollupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState<FrequencyFilter>('all');

  useEffect(() => {
    const donorsQuery = query(collection(db, 'artifacts', appId, 'donors'), orderBy('lastDonationAt', 'desc'));
    const unsubscribe = onSnapshot(
      donorsQuery,
      (snapshot) => {
        const mapped: DonorRollupRecord[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as Partial<DonorRollupRecord>;
          return {
            id: docSnap.id,
            userId: data.userId ?? null,
            stripeCustomerId: data.stripeCustomerId ?? null,
            email: data.email ?? null,
            name: data.name ?? null,
            totalDonated: data.totalDonated ?? 0,
            donationCount: data.donationCount ?? 0,
            monthlyDonationCount: data.monthlyDonationCount ?? 0,
            oneTimeDonationCount: data.oneTimeDonationCount ?? 0,
            lastDonationAt: data.lastDonationAt ?? null,
            lastDonationAmount: data.lastDonationAmount ?? 0,
            lastDonationFrequency: data.lastDonationFrequency ?? null,
            currency: data.currency ?? 'usd',
          };
        });
        setDonors(mapped);
        setLoading(false);
      },
      () => {
        setDonors([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredDonors = useMemo(() => {
    return donors.filter((donor) => {
      if (frequencyFilter !== 'all' && donor.lastDonationFrequency !== frequencyFilter) return false;
      if (!search.trim()) return true;

      const needle = search.toLowerCase();
      return (
        (donor.name ?? '').toLowerCase().includes(needle) ||
        (donor.email ?? '').toLowerCase().includes(needle) ||
        (donor.userId ?? '').toLowerCase().includes(needle) ||
        donor.id.toLowerCase().includes(needle)
      );
    });
  }, [donors, frequencyFilter, search]);

  const totals = useMemo(() => {
    return donors.reduce(
      (acc, donor) => {
        acc.amount += donor.totalDonated;
        acc.donations += donor.donationCount;
        return acc;
      },
      { amount: 0, donations: 0 }
    );
  }, [donors]);

  return (
    <section className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <HeartHandshake size={20} className="text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900">Donors Management</h1>
        </div>
        <p className="text-sm text-gray-600">
          Track donation history and donor rollups synced from Stripe webhook events.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Donors</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{donors.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Donations</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{totals.donations}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Lifetime Amount</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{formatMoney(totals.amount, 'usd')}</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by donor name, email, user ID, or donor key"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </label>
        <select
          value={frequencyFilter}
          onChange={(event) => setFrequencyFilter(event.target.value as FrequencyFilter)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">All donor types</option>
          <option value="monthly">Monthly donors</option>
          <option value="one_time">One-time donors</option>
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-8 text-sm text-gray-600 text-center">Loading donor records...</div>
        ) : filteredDonors.length === 0 ? (
          <div className="p-8 text-sm text-gray-600 text-center">
            No donor records found for the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Donor</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Lifetime</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Donations</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Last Donation</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Last Donated At</th>
              </tr>
            </thead>
            <tbody>
              {filteredDonors.map((donor) => (
                <tr key={donor.id} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{donor.name || donor.email || donor.id}</span>
                      <span className="text-xs text-gray-500">{donor.email ?? donor.userId ?? donor.id}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        donor.lastDonationFrequency === 'monthly'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {donor.lastDonationFrequency === 'monthly' ? 'Monthly' : 'One-time'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatMoney(donor.totalDonated, donor.currency)}</td>
                  <td className="px-4 py-3 text-gray-700">{donor.donationCount}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {formatMoney(donor.lastDonationAmount, donor.currency)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatDateTime(donor.lastDonationAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
